// Phase 86AZ.60 — Public contractor recommendation form
// =======================================================
// /muteahhit-oner/:token — the recommender lands here from their welcome
// email and submits contractor candidates (company, contact, category,
// note, optional presentation file). No login. No password. Just the
// token-gated form. Recommended contractor does NOT receive an email —
// admin reviews and decides next steps.
import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, CheckCircle2, AlertCircle, HardHat, Upload, X,
  Building2, Mail, Phone, Tag, FileText, Clock, ChevronDown, Bookmark, User, Trash2, Globe, Pencil, Users, ArrowRight,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const ALLOWED_EXTS = ['pdf', 'ppt', 'pptx'];

const StatusView = ({ icon: Icon, title, message, tone = 'red' }) => {
  const ring = tone === 'red' ? 'bg-red-100 text-red-600'
             : tone === 'emerald' ? 'bg-emerald-100 text-emerald-600'
             : 'bg-amber-100 text-amber-600';
  return (
    <div className="min-h-screen bg-[#F7F5EF] flex items-center justify-center p-4 notranslate" translate="no">
      <div className="bg-white rounded-3xl shadow-xl border border-[rgba(10,58,90,0.08)] p-10 max-w-md w-full text-center">
        <div className={`h-20 w-20 ${ring} rounded-full flex items-center justify-center mx-auto mb-5`}>
          <Icon className="h-10 w-10" />
        </div>
        <h2 className="font-playfair text-2xl md:text-3xl text-[#0A3A5A] font-medium mb-3 tracking-tight">{title}</h2>
        <p className="text-[#4A5568] text-sm mb-6 leading-relaxed">{message}</p>
        <Link to="/" className="inline-block bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-2.5 text-sm font-semibold">
          Ana Sayfa
        </Link>
      </div>
    </div>
  );
};

export default function MuteahhitOner() {
  const { token } = useParams();
  const { language, setLanguage } = useLanguage();
  const tr = language === 'tr';
  const fileRef = useRef(null);

  // Phase 86AZ.60.3 — Default this public landing to Turkish on first mount.
  // The recommender opens the link directly from their email, so we shouldn't
  // honor a stale browser preference (which could be EN). They can still
  // toggle to EN via the top-right LanguageToggle.
  useEffect(() => {
    if (!sessionStorage.getItem('tedarikci_oner_lang_seen')) {
      setLanguage?.('tr');
      sessionStorage.setItem('tedarikci_oner_lang_seen', '1');
    }
  }, [setLanguage]);

  const [pageState, setPageState] = useState('loading'); // loading | form | invalid | expired
  const [recommender, setRecommender] = useState(null);
  // Phase 86AZ.60.5 — Full submission history for this recommender (loaded
  // from /my-candidates on mount, then prepended after each successful
  // submit). Each entry can be deleted by its owner.
  const [history, setHistory] = useState([]);
  // Phase 86AZ.60 / Feb 2026 — Tedarikçi categories (renamed from contractor;
  // now sources the same list as /tedarikci-kayit so referrers can recommend
  // candidates across the entire supplier taxonomy, not just contractors).
  const [categories, setCategories] = useState([]);

  const [form, setForm] = useState({
    company: '', contact_name: '', contact_email: '', contact_phone: '',
    category: '', website: '', note: '',
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  // Phase 86AZ.60.6 — Centered confirmation modal for delete (replaces the
  // browser-native `window.confirm()` which renders at the very top of the
  // viewport and feels jarring).
  const [confirmDelete, setConfirmDelete] = useState(null); // candidate row | null
  const [deleting, setDeleting] = useState(false);
  // Phase 86AZ.60.7 — "Daha fazla bilgi" toggle. Only company name is
  // required by default; the rest of the fields are collapsed until the
  // recommender explicitly opts in.
  const [showMore, setShowMore] = useState(false);
  // Phase 86AZ.60.8 — Edit mode. When set, the form updates an existing
  // candidate (PUT) instead of creating a new one. `existingPresentation`
  // keeps the currently-attached file's name so the user can keep, replace
  // or remove it. `removePresentation` flags an explicit removal.
  const [editingId, setEditingId] = useState(null);
  const [existingPresentation, setExistingPresentation] = useState(null); // {name} | null
  const [removePresentation, setRemovePresentation] = useState(false);
  const formCardRef = useRef(null);

  useEffect(() => {
    const v = async () => {
      try {
        const r = await axios.get(`${API}/contractor-recommenders/validate`, { params: { token } });
        setRecommender(r.data);
        setPageState('form');
        // Load history right after a successful validate.
        try {
          const h = await axios.get(`${API}/contractor-recommenders/my-candidates`, { params: { token } });
          setHistory(h.data || []);
        } catch { /* swallow — history is non-fatal */ }
      } catch (err) {
        const status = err.response?.status;
        setPageState(status === 410 ? 'expired' : 'invalid');
      }
    };
    v();
  }, [token]);

  // Load tedarikçi (supplier) categories once (same list as /tedarikci-kayit).
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/public/supplier-categories`);
        setCategories(r.data || []);
      } catch {
        setCategories([{ id: 'default', name: 'Stand Constructor' }]);
      }
    })();
  }, []);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setFileError('');
    if (!f) { setFile(null); return; }
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      setFileError(tr ? 'Yalnızca PDF, PPT veya PPTX dosyaları yüklenebilir.' : 'Only PDF, PPT or PPTX files are allowed.');
      e.target.value = '';
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError(tr ? 'Dosya 25 MB sınırını aşıyor.' : 'File exceeds the 25 MB limit.');
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    setFileError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteCandidate = (cand) => setConfirmDelete(cand);

  const startEdit = (cand) => {
    setEditingId(cand.id);
    setForm({
      company:       cand.company || '',
      contact_name:  cand.contact_name || '',
      contact_email: cand.contact_email || '',
      contact_phone: cand.contact_phone || '',
      category:      cand.category || '',
      website:       cand.website || '',
      note:          cand.note || '',
    });
    setExistingPresentation(cand.presentation_name ? { name: cand.presentation_name } : null);
    setRemovePresentation(false);
    clearFile();
    setShowMore(true);
    setError(''); setFieldErrors({});
    setTimeout(() => formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ company: '', contact_name: '', contact_email: '', contact_phone: '', category: '', website: '', note: '' });
    setExistingPresentation(null);
    setRemovePresentation(false);
    clearFile();
    setError(''); setFieldErrors({});
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/contractor-recommenders/my-candidates/${confirmDelete.id}`, { params: { token } });
      setHistory(prev => prev.filter(c => c.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      setError(err.response?.data?.detail || (tr ? 'Silinemedi.' : 'Could not delete.'));
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(''); setFieldErrors({});
    const fe = {};
    if (!form.company.trim()) fe.company = tr ? 'Firma adı zorunlu.' : 'Company is required.';
    // Email is optional now — but if user provided one, validate format.
    if (form.contact_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email))
      fe.contact_email = tr ? 'Geçerli e-posta giriniz.' : 'Invalid email.';
    if (Object.keys(fe).length) { setFieldErrors(fe); return; }

    const fd = new FormData();
    fd.append('token',         token);
    fd.append('company',       form.company.trim());
    fd.append('contact_name',  form.contact_name.trim());
    fd.append('contact_email', form.contact_email.trim().toLowerCase());
    fd.append('contact_phone', form.contact_phone.trim());
    fd.append('category',      form.category.trim());
    fd.append('website',       form.website.trim());
    fd.append('note',          form.note.trim());
    if (file) fd.append('presentation', file);
    if (editingId && removePresentation && !file) fd.append('remove_presentation', '1');

    setSubmitting(true);
    try {
      if (editingId) {
        const r = await axios.put(`${API}/contractor-recommenders/my-candidates/${editingId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setHistory(prev => prev.map(c => (c.id === editingId ? r.data : c)));
        cancelEdit();
      } else {
        const r = await axios.post(`${API}/contractor-recommenders/submit-candidate`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setHistory(prev => [r.data, ...prev]);
        setForm({ company: '', contact_name: '', contact_email: '', contact_phone: '', category: '', website: '', note: '' });
        clearFile();
      }
    } catch (err) {
      setError(err.response?.data?.detail || (tr ? 'Gönderim başarısız oldu.' : 'Submission failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-[#F7F5EF] flex items-center justify-center notranslate" translate="no">
        <div className="text-center">
          <div className="h-10 w-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5A6B7D] text-sm">{tr ? 'Bağlantı doğrulanıyor...' : 'Validating link...'}</p>
        </div>
      </div>
    );
  }
  if (pageState === 'expired') {
    return <StatusView icon={Clock} title={tr ? 'Bağlantı Süresi Doldu' : 'Link Expired'}
      message={tr ? 'Önerme bağlantınızın geçerlilik süresi dolmuştur. Lütfen Ultra Admin ile iletişime geçin.' : 'Your recommendation link has expired. Please contact the Ultra Admin.'} tone="amber" />;
  }
  if (pageState === 'invalid') {
    return <StatusView icon={AlertCircle} title={tr ? 'Geçersiz Bağlantı' : 'Invalid Link'}
      message={tr ? 'Bu bağlantı geçersiz. Lütfen e-postanızdaki tam URL\'yi kullanın.' : 'This link is not valid. Please use the full URL from your email.'} tone="red" />;
  }

  return (
    <div className="min-h-screen bg-[#F7F5EF] flex flex-col notranslate" translate="no">
      <div className="flex items-center justify-between px-6 py-4 shrink-0 border-b border-[rgba(10,58,90,0.06)] bg-white">
        <Link to="/" className="flex items-center gap-2 text-[#0A3A5A]/70 hover:text-[#0A3A5A] text-sm font-medium">
          <ArrowLeft className="h-4 w-4" /> {tr ? 'Ana Sayfa' : 'Home'}
        </Link>
        <div className="text-[#0A3A5A] font-playfair text-2xl tracking-tight italic">COP 31 · Antalya</div>
        <LanguageToggle variant="default" />
      </div>

      <div className="flex-1 flex items-start justify-center px-4 pb-12 pt-6">
        <div className="w-full max-w-2xl">
          {/* Hero */}
          <div className="text-center mb-7">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-red-100 border border-red-200 mb-3">
              <HardHat className="h-7 w-7 text-red-600" />
            </div>
            <h1 className="font-playfair text-3xl md:text-4xl text-[#0A3A5A] font-medium mb-1 tracking-tight">
              {tr ? 'Tedarikçi Adayı Öner' : 'Recommend a Supplier'}
            </h1>
            <p className="text-[#4A5568] text-sm">
              {tr ? <>Hoş geldiniz, <strong>{recommender?.full_name}</strong>.<br/>COP 31 Antalya için tanıdığınız tedarikçi firmaları öneri olarak iletebilirsiniz.</>
                  : <>Welcome, <strong>{recommender?.full_name}</strong>.<br/>You can recommend supplier firms you know for COP 31 Antalya.</>}
            </p>
            {/* Phase 86AZ.60.4 — Bookmark hint: encourage the recommender to
                save this link so they can return later without digging up the
                original invitation email. */}
            <div className="mt-4 inline-flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2 text-left max-w-md mx-auto" data-testid="oner-bookmark-hint">
              <Bookmark className="h-3.5 w-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
              <p className="text-[12px] text-amber-800 leading-snug">
                {tr ? <>Bu sayfaya <strong>istediğiniz zaman bu linkten geri dönebilirsiniz</strong> · 1 yıl geçerli</>
                    : <>You can <strong>return to this page anytime via this link</strong> · valid for 1 year</>}
              </p>
            </div>

            {/* Phase 86AZ.60.9 — Tüm önerilen firmaları (adaylar) görüntüleme */}
            <div className="mt-4">
              <Link
                to="/tedarikci/adaylar"
                data-testid="oner-view-all-candidates"
                className="inline-flex items-center gap-2 bg-[#0A3A5A] hover:bg-[#0c4669] text-white rounded-full px-6 py-2.5 text-sm font-semibold transition-colors shadow-sm">
                <Users className="h-4 w-4" />
                {tr ? 'Tüm Önerilen Firmaları Gör' : 'View All Recommended Firms'}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Form card */}
          <form ref={formCardRef} onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl border border-[rgba(10,58,90,0.08)] overflow-hidden">
            {editingId ? (
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 text-xs text-amber-800 flex items-center justify-between gap-2" data-testid="oner-edit-banner">
                <span className="flex items-center gap-2">
                  <Pencil className="h-3.5 w-3.5 flex-shrink-0" />
                  {tr ? 'Mevcut bir öneriyi düzenliyorsunuz.' : 'You are editing an existing recommendation.'}
                </span>
                <button type="button" onClick={cancelEdit} className="text-amber-800 hover:text-amber-900 font-semibold underline" data-testid="oner-edit-cancel">
                  {tr ? 'Vazgeç' : 'Cancel'}
                </button>
              </div>
            ) : (
              <div className="bg-red-50 border-b border-red-100 px-6 py-3 text-xs text-red-800 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{tr ? 'Bu yalnızca bir öneri alanıdır. Önerdiğiniz firmaya otomatik e-posta gitmez; öneriniz organizasyon komitesi tarafından incelenir.' : 'This is a recommendation channel only. No automatic email is sent to the recommended firm; the organizing committee reviews your submission.'}</span>
              </div>
            )}

            <div className="p-6 space-y-4">
              <div>
                <Label className="text-sm text-gray-700">{tr ? 'Firma Adı' : 'Company Name'} <span className="text-red-500">*</span></Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input data-testid="oner-company" className={`pl-9 ${fieldErrors.company ? 'border-red-400' : ''}`} value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} />
                </div>
                {fieldErrors.company && <p className="text-xs text-red-500 mt-1">{fieldErrors.company}</p>}
              </div>

              {/* Phase 86AZ.60.7 — Daha fazla bilgi toggle */}
              <label className="flex items-center gap-2 cursor-pointer select-none py-1" data-testid="oner-show-more-toggle">
                <input
                  type="checkbox"
                  checked={showMore}
                  onChange={e => setShowMore(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-300"
                />
                <span className="text-sm text-gray-700">
                  {tr ? 'Daha fazla bilgi ekle (opsiyonel)' : 'Add more info (optional)'}
                </span>
                <span className="text-[11px] text-gray-400 ml-1">
                  {tr ? '— yetkili, e-posta, telefon, kategori, web sitesi, not, sunum' : '— contact, email, phone, category, website, note, presentation'}
                </span>
              </label>

              {showMore && (<>

              <div>
                <Label className="text-sm text-gray-700">{tr ? 'Yetkili Adı Soyadı' : 'Contact Person'}</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input data-testid="oner-contact-name" className="pl-9" placeholder={tr ? 'Firmadaki muhatap kişi' : 'Contact person at the firm'} value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-700">{tr ? 'İletişim E-postası' : 'Contact Email'}</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input data-testid="oner-email" type="email" className={`pl-9 ${fieldErrors.contact_email ? 'border-red-400' : ''}`} value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))} />
                  </div>
                  {fieldErrors.contact_email && <p className="text-xs text-red-500 mt-1">{fieldErrors.contact_email}</p>}
                </div>
                <div>
                  <Label className="text-sm text-gray-700">{tr ? 'İletişim Telefonu' : 'Contact Phone'}</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input data-testid="oner-phone" className="pl-9" placeholder="+90 ..." value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))} />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm text-gray-700">{tr ? 'Kategori / Uzmanlık' : 'Category / Discipline'}</Label>
                <div className="relative mt-1">
                  <Tag className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                  <select
                    data-testid="oner-category"
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-300 appearance-none text-gray-700">
                    <option value="">{tr ? '-- Kategori Seçin --' : '-- Select Category --'}</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <Label className="text-sm text-gray-700">{tr ? 'Web Sitesi' : 'Website'}</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    data-testid="oner-website"
                    className="pl-9"
                    placeholder="www.firmaadi.com"
                    value={form.website}
                    onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-gray-700">{tr ? 'Notunuz' : 'Your Note'}</Label>
                <textarea
                  data-testid="oner-note"
                  rows={3}
                  className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                  placeholder={tr ? 'Bu firmayı niye öneriyorsunuz? Önemli referansları?' : 'Why are you recommending them? Notable references?'}
                  value={form.note}
                  onChange={e => setForm(f => ({...f, note: e.target.value}))}
                />
              </div>

              {/* File upload */}
              <div>
                <Label className="text-sm text-gray-700">{tr ? 'Firma Sunumu (PDF, PPT, PPTX · max 25 MB)' : 'Company Presentation (PDF, PPT, PPTX · max 25 MB)'}</Label>
                {editingId && existingPresentation && !file && !removePresentation ? (
                  <div className="mt-1 flex items-center gap-3 border border-amber-200 bg-amber-50 rounded-xl px-4 py-3" data-testid="oner-existing-file">
                    <FileText className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{existingPresentation.name}</p>
                      <p className="text-[11px] text-gray-500">{tr ? 'Mevcut sunum · değiştirmek için yeni dosya seçin' : 'Current file · choose a new one to replace'}</p>
                    </div>
                    <button type="button" onClick={() => setRemovePresentation(true)} className="text-gray-400 hover:text-red-600" title={tr ? 'Sunumu kaldır' : 'Remove presentation'} data-testid="oner-remove-existing-file">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : !file ? (
                  <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-red-300 rounded-xl py-6 cursor-pointer transition-colors">
                    <Upload className="h-5 w-5 text-gray-400 mb-2" />
                    <span className="text-xs text-gray-500">{tr ? 'Dosya seçmek için tıklayın veya sürükleyin' : 'Click or drag a file to upload'}</span>
                    <input data-testid="oner-file" ref={fileRef} type="file" className="hidden" accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" onChange={onFileChange} />
                  </label>
                ) : (
                  <div className="mt-1 flex items-center gap-3 border border-red-200 bg-red-50 rounded-xl px-4 py-3">
                    <FileText className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate" data-testid="oner-file-name">{file.name}</p>
                      <p className="text-[11px] text-gray-500">{(file.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button type="button" onClick={clearFile} className="text-gray-400 hover:text-red-600">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {fileError && <p className="text-xs text-red-500 mt-1" data-testid="oner-file-err">{fileError}</p>}
              </div>

              </>)}

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <p className="text-red-600 text-sm" data-testid="oner-error">{error}</p>
                </div>
              )}

              <Button
                data-testid="oner-submit"
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl py-5 font-semibold text-sm">
                {submitting
                  ? <span className="flex items-center justify-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{editingId ? (tr ? 'Güncelleniyor...' : 'Updating...') : (tr ? 'Gönderiliyor...' : 'Submitting...')}</span>
                  : (editingId ? (tr ? 'Öneriyi Güncelle' : 'Update Recommendation') : (tr ? 'Öneriyi Gönder' : 'Submit Recommendation'))}
              </Button>
            </div>
          </form>

          {/* Phase 86AZ.60.5 — Full submission history (persistent across
              visits via the same recommender token). The recommender can
              delete their own past entries. */}
          {history.length > 0 && (
            <div className="mt-7 bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-[#0A3A5A] uppercase tracking-wide flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  {tr ? `Önerileriniz (${history.length})` : `Your Recommendations (${history.length})`}
                </p>
                <span className="text-[10px] text-gray-400">{tr ? 'Bu listeyi yalnızca siz görüyorsunuz' : 'Only you can see this list'}</span>
              </div>
              <Link to="/tedarikci/adaylar" className="text-xs font-semibold text-[#0A3A5A] hover:underline flex items-center gap-1 mb-3" data-testid="oner-view-all-candidates-history">
                <Users className="h-3.5 w-3.5" />
                {tr ? 'Tüm önerilen firmaları gör' : 'View all recommended firms'}
                <ArrowRight className="h-3 w-3" />
              </Link>
              <ul className="space-y-2">
                {history.map(s => (
                  <li key={s.id} className="flex items-center justify-between gap-3 text-sm border-b border-gray-100 last:border-0 pb-2 last:pb-0" data-testid={`oner-history-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-800 truncate">{s.company}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {s.contact_name ? <>{s.contact_name} · </> : null}
                        {s.contact_email}
                        {s.category ? <> · {s.category}</> : null}
                      </p>
                      {s.presentation_name && (
                        <p className="text-[11px] text-red-600 mt-0.5 flex items-center gap-1 truncate">
                          <FileText className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{s.presentation_name}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[11px] text-gray-400">{(s.created_at || '').slice(0, 10)}</span>
                      <button
                        type="button"
                        onClick={() => startEdit(s)}
                        className={`h-7 w-7 rounded-full flex items-center justify-center ${editingId === s.id ? 'bg-amber-100 text-amber-700' : 'text-[#0A3A5A] hover:bg-blue-50'}`}
                        title={tr ? 'Bu öneriyi düzenle' : 'Edit this recommendation'}
                        data-testid={`oner-history-edit-${s.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteCandidate(s)}
                        className="h-7 w-7 rounded-full text-red-600 hover:bg-red-50 flex items-center justify-center"
                        title={tr ? 'Bu öneriyi sil' : 'Delete this recommendation'}
                        data-testid={`oner-history-delete-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="py-5 text-center text-[11px] text-[#5A6B7D] bg-white border-t border-[rgba(10,58,90,0.06)]">© 2026 COP 31 · Antalya</div>

      {/* Phase 86AZ.60.6 — Centered delete-confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/55 p-4" data-testid="oner-delete-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-700" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-800 text-lg">{tr ? 'Öneriyi Sil' : 'Delete Recommendation'}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {tr
                    ? <><strong>{confirmDelete.company}</strong> firmasının önerisini kalıcı olarak silmek istediğinize emin misiniz?</>
                    : <><strong>{confirmDelete.company}</strong> recommendation will be permanently deleted. Are you sure?</>}
                </p>
                {confirmDelete.presentation_name && (
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    {tr ? 'Yüklenen sunum da silinecek: ' : 'Uploaded presentation will also be removed: '}
                    <span className="font-medium truncate">{confirmDelete.presentation_name}</span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                data-testid="oner-delete-cancel">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={performDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="oner-delete-confirm">
                {deleting
                  ? (tr ? 'Siliniyor…' : 'Deleting…')
                  : (tr ? 'Evet, Sil' : 'Yes, Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
